// import { useState } from 'react';
import { motion } from 'framer-motion';
import { useSettings } from '../hooks/useSettings';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../components/ui/Button';
import { Award, Bell, Lock, BookOpen, Mic, RefreshCw, Trash2, Save, Star, Shield, Target, Volume2, AlertTriangle, Wand2, TrendingUp, ArrowRight, AudioWaveform } from 'lucide-react';

const SectionCard = ({ title, icon, children, color }: { title: string, icon: React.ReactNode, children: React.ReactNode, color: string }) => (
    <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-3xl shadow-xl overflow-hidden border-t-8" style={{ borderTopColor: color }}>
        <div className="p-6 flex items-center space-x-4">
            <div className="text-4xl" style={{ color }}>{icon}</div>
            <h3 className="text-3xl font-bold text-gray-800">{title}</h3>
        </div>
        <div className="p-8 pt-4 space-y-8">
            {children}
        </div>
    </motion.div>
);

const ToggleSwitch = ({ label, description, checked, onChange, icon }: { label: string, description: string, checked: boolean, onChange: (checked: boolean) => void, icon: React.ReactNode }) => (
    <div className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors">
        <div className="flex items-center space-x-4">
            <div className="text-3xl text-purple-500">{icon}</div>
            <div>
                <span className="text-xl font-semibold text-gray-800">{label}</span>
                <p className="text-gray-500">{description}</p>
            </div>
        </div>
        <button
            onClick={() => onChange(!checked)}
            className={`relative inline-flex items-center h-10 w-20 rounded-full transition-colors duration-300 ease-in-out shadow-inner ${checked ? 'bg-green-400' : 'bg-gray-300'}`}>
            <motion.span
                layout
                transition={{ type: "spring", stiffness: 700, damping: 30 }}
                className={`inline-block w-8 h-8 transform bg-white rounded-full shadow-md ${checked ? 'translate-x-11' : 'translate-x-1'}`} />
        </button>
    </div>
);

const Slider = ({ label, value, min, max, step, onChange, icon }: { label: string, value: number, min: number, max: number, step: number, onChange: (value: number) => void, icon: React.ReactNode }) => (
    <div className="p-4 rounded-2xl bg-gray-50">
        <div className="flex items-center space-x-4 mb-3">
            <div className="text-3xl text-purple-500">{icon}</div>
            <div>
                <label className="text-xl font-semibold text-gray-800">{label}</label>
                <p className="text-gray-500">Current value: <span className="font-bold text-purple-600 text-lg">{value}</span></p>
            </div>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full h-4 bg-gray-200 rounded-full appearance-none cursor-pointer accent-purple-500"
        />
    </div>
);

const Select = ({ label, value, options, onChange, icon }: { label: string, value: string, options: { value: string, label: string }[], onChange: (value: string) => void, icon: React.ReactNode }) => (
    <div className="p-4 rounded-2xl bg-gray-50">
        <div className="flex items-center space-x-4 mb-3">
            <div className="text-3xl text-purple-500">{icon}</div>
            <label className="text-xl font-semibold text-gray-800">{label}</label>
        </div>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-5 py-4 text-lg border-2 border-gray-200 rounded-full focus:outline-none focus:ring-4 focus:ring-purple-300 transition bg-white">
            {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
    </div>
);

export default function SettingsPage() {
    const { settings, isLoading, isSaving, updateSetting, resetToDefaults, deleteAccount, error } = useSettings();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-blue-100">
                <div className="text-center">
                    <LoadingSpinner size="lg" className="mb-4" />
                    <p className="text-lg text-blue-700 font-semibold">Loading Settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-100 to-purple-200 font-sans p-4 sm:p-6 lg:p-8">
            <div className="max-w-5xl mx-auto">
                <header className="text-center mb-16">
                    <motion.h1
                        initial={{ y: -50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 100 }}
                        className="text-7xl font-extrabold text-purple-800 tracking-tight drop-shadow-lg">Settings</motion.h1>
                    <motion.p
                        initial={{ y: -30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, type: 'spring' }}
                        className="text-gray-600 mt-4 text-2xl">Make SayIt truly yours! ✨</motion.p>
                </header>

                {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-lg">Error: {error.message}</div>}

                <div className="space-y-12">
                    <SectionCard title="Notifications" icon={<Bell />} color="#3b82f6">
                        <ToggleSwitch label="Daily Reminders" description="Get a nudge to practice every day" checked={settings.notifications.daily_reminders} onChange={(val) => updateSetting('notifications', 'daily_reminders', val)} icon={<Star />} />
                        <ToggleSwitch label="Achievement Alerts" description="Celebrate when you unlock a new badge" checked={settings.notifications.achievement_alerts} onChange={(val) => updateSetting('notifications', 'achievement_alerts', val)} icon={<Award />} />
                        <ToggleSwitch label="Streak Warnings" description="A heads-up before your streak is lost" checked={settings.notifications.streak_warnings} onChange={(val) => updateSetting('notifications', 'streak_warnings', val)} icon={<AlertTriangle />} />
                        <ToggleSwitch label="Email Updates" description="Receive news and tips in your inbox" checked={settings.notifications.email_updates} onChange={(val) => updateSetting('notifications', 'email_updates', val)} icon={<Bell />} />
                    </SectionCard>

                    <SectionCard title="Privacy" icon={<Lock />} color="#10b981">
                        <Select label="Profile Visibility" value={settings.privacy.profile_visibility} onChange={(val) => updateSetting('privacy', 'profile_visibility', val)} options={[
                            { value: 'public', label: 'Public (Everyone can see)' },
                            { value: 'friends', label: 'Friends Only (Coming Soon)' },
                            { value: 'private', label: 'Private (Only you can see)' },
                        ]} icon={<Shield />} />
                        <ToggleSwitch label="Show My Progress" description="Let others see your learning journey" checked={settings.privacy.show_progress} onChange={(val) => updateSetting('privacy', 'show_progress', val)} icon={<TrendingUp />} />
                        <ToggleSwitch label="Show My Achievements" description="Show off your hard-earned badges" checked={settings.privacy.show_achievements} onChange={(val) => updateSetting('privacy', 'show_achievements', val)} icon={<Award />} />
                    </SectionCard>

                    <SectionCard title="Learning" icon={<BookOpen />} color="#f59e0b">
                        <Select label="Difficulty Level" value={settings.learning.difficulty_level} onChange={(val) => updateSetting('learning', 'difficulty_level', val)} options={[
                            { value: 'beginner', label: '🌱 Beginner' },
                            { value: 'intermediate', label: '🌳 Intermediate' },
                            { value: 'advanced', label: '🚀 Advanced' },
                        ]} icon={<Wand2 />} />
                        <Slider label="Daily Goal" value={settings.learning.daily_goal} min={5} max={50} step={5} onChange={(val) => updateSetting('learning', 'daily_goal', val)} icon={<Target />} />
                        <ToggleSwitch label="Auto-Advance" description="Move to the next word automatically" checked={settings.learning.auto_advance} onChange={(val) => updateSetting('learning', 'auto_advance', val)} icon={<ArrowRight />} />
                    </SectionCard>

                    <SectionCard title="Audio" icon={<Mic />} color="#ef4444">
                        <Slider label="Playback Speed" value={settings.audio.playback_speed} min={0.5} max={1.5} step={0.25} onChange={(val) => updateSetting('audio', 'playback_speed', val)} icon={<Volume2 />} />
                        <ToggleSwitch label="Voice Feedback" description="Hear audio cues and confirmations" checked={settings.audio.voice_feedback} onChange={(val) => updateSetting('audio', 'voice_feedback', val)} icon={<Mic />} />
                        <Slider label="Microphone Sensitivity" value={settings.audio.microphone_sensitivity} min={0.1} max={1.0} step={0.1} onChange={(val) => updateSetting('audio', 'microphone_sensitivity', val)} icon={<AudioWaveform />} />
                    </SectionCard>

                    <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl p-8 mt-8 border-t-8 border-gray-400">
                        <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">Manage Account</h3>
                        <div className="space-y-6">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-gray-100">
                                <p className="text-gray-600 text-lg">Reset all settings to their defaults.</p>
                                <Button variant="outline" onClick={resetToDefaults} className="rounded-full px-8 py-3 text-lg font-semibold">
                                    <RefreshCw className="w-5 h-5 mr-2" />
                                    Reset Settings
                                </Button>
                            </div>
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-red-50 border-2 border-red-200">
                                <p className="text-red-700 font-semibold text-lg">Permanently delete your account.</p>
                                <Button variant="destructive" onClick={() => window.confirm('Are you absolutely sure? This cannot be undone!') && deleteAccount()} className="rounded-full px-8 py-3 text-lg font-semibold">
                                    <Trash2 className="w-5 h-5 mr-2" />
                                    Delete Account
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="mt-16 text-center">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button onClick={() => {}} loading={isSaving} size="lg" className="rounded-full px-16 py-5 text-2xl font-bold shadow-2xl bg-purple-600 hover:bg-purple-700 transition-all transform hover:-translate-y-1">
                            <Save className="w-7 h-7 mr-3" />
                            {isSaving ? 'Saving...' : 'Save All Changes'}
                        </Button>
                    </motion.div>
                </footer>
            </div>
        </div>
    );
}